import { useTranslation } from "react-i18next";
import { Grid } from "@mui/material";
import { useCallback, useState } from "react";

import {
  FormContainer,
  FormInput,
  ISelectType,
  SwitchInput,
  Treturn,
  useFetch,
} from "@4uhub/lib4uhub";

import MainApiService from "../../../../../services/mainApi.service";
import SiteLoginContentLanguages from "./SiteLoginContentLanguages";
import {
  SiteLoginContentSchema,
  TSiteLoginContentForm,
} from "./SiteLoginContentSchema";
import {
  IGetSiteLoginContent,
  ISiteLoginContentPayload,
} from "../../../../../models/site-login-content";

const SITE_LOGIN_CONTENT_ROUTE =
  process.env.REACT_APP_MAIN + "/api/v1/SiteLoginContent/";
const siteLoginContentService = new MainApiService<IGetSiteLoginContent>(
  SITE_LOGIN_CONTENT_ROUTE
);
const siteLoginContentSaveService = new MainApiService<ISiteLoginContentPayload>(
  SITE_LOGIN_CONTENT_ROUTE
);

const LANGUAGES_ROUTE = process.env.REACT_APP_MAIN + "/api/v1/Language/";
const languageService = new MainApiService<ISelectType>(LANGUAGES_ROUTE);

const translationPath = "page.4uSiteApp.siteLoginContent.";

const SiteLoginContent = () => {
  const { t } = useTranslation();

  const [, setDefaultLanguage] = useState<ISelectType[]>();

  const { sendRequest: listLanguages } = useFetch(languageService.list);

  const { sendRequest: save, loading: sLoading } = useFetch(
    siteLoginContentSaveService.create
  );
  const { sendRequest: update, loading: uLoading } = useFetch(
    siteLoginContentSaveService.updateItemById
  );
  const { sendRequest: get, loading: gLoading } = useFetch(
    siteLoginContentService.getItemById
  );

  const defaultValues = async () => {
    const { data } = await listLanguages(null);
    if (data) {
      setDefaultLanguage(data);
    }
    return {
      name: "",
      presentationOrder: 1,
      enable: true,
      linkUrl: "",
      siteLoginContentLanguages: [
        {
          language: data ? data[0] : null,
          documentText: "",
          linkLabel: "",
        },
      ],
    };
  };

  const handleGetFunction = useCallback(
    async (id: string): Promise<Treturn<TSiteLoginContentForm>> => {
      const { data, success } = await get(id);
      if (data) {
        return {
          data: {
            name: data.name,
            presentationOrder: data.presentationOrder,
            enable: data.enable,
            linkUrl: data.linkUrl ?? "",
            siteLoginContentLanguages: data.siteLoginContentLanguages.map(
              (language) => ({
                language: language.language,
                documentText: language.documentText,
                linkLabel: language.linkLabel ?? "",
              })
            ),
          } as TSiteLoginContentForm,
          success: true,
        };
      }
      return { data, success } as Treturn<TSiteLoginContentForm>;
    },
    [get]
  );

  const buildPayload = (v: TSiteLoginContentForm): ISiteLoginContentPayload => ({
    name: v.name,
    presentationOrder: v.presentationOrder,
    enable: v.enable,
    linkUrl: v.linkUrl ? v.linkUrl : null,
    siteLoginContentLanguages: v.siteLoginContentLanguages.map((language) => ({
      languageId: language.language ? language.language.id : "",
      documentText: language.documentText,
      linkLabel: language.linkLabel ? language.linkLabel : null,
    })),
  });

  const handleSave = (v: TSiteLoginContentForm) => {
    return save(buildPayload(v));
  };

  const handleUpdate = (v: TSiteLoginContentForm, id: string) => {
    return update({
      item: { ...buildPayload(v), id: id },
      id: id,
    });
  };

  const loading = sLoading || uLoading || gLoading;

  return (
    <FormContainer<TSiteLoginContentForm>
      loading={loading}
      title={t(translationPath + "site_login_content")}
      subtitle={t(translationPath + "site_login_content") || ""}
      saveFunction={handleSave}
      updateFunction={handleUpdate}
      getFunction={handleGetFunction}
      schema={SiteLoginContentSchema}
      defaultValues={defaultValues}
      fields={() => (
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <FormInput
              size="small"
              name="name"
              label={t(translationPath + "name")}
              fullWidth
              autoFocus
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormInput
              size="small"
              name="presentationOrder"
              type="number"
              label={t(translationPath + "presentation_order")}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={2}>
            <SwitchInput
              name="enable"
              label={t(translationPath + "enable") || "Enable"}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormInput
              size="small"
              name="linkUrl"
              label={t(translationPath + "link_url")}
              fullWidth
            />
          </Grid>

          <SiteLoginContentLanguages service={languageService.list} />
        </Grid>
      )}
    />
  );
};

export default SiteLoginContent;
